import React, { FunctionComponent, useMemo } from 'react'
import { ProductSelectionCard } from './ProductSelectionCard'
import { Callback } from '../../../utils/types'
import classNames from 'classnames'
import { calculateProductPricing } from '../../../helpers/calculateProductPricing'
import { calculateSurcharges } from '../../../helpers/calculateSurcharges'

export interface Props {
  data: any
  selected?: boolean
  onSelect: Callback
  selectedSurcharges: any
  discount?: { amount: number; type: '%' | '#' }
}

const ProductInfoRow = ({ title, value, stickyTitle = false }) => {
  const titleClass = classNames({ 'product-info__title': stickyTitle })
  return (
    <div className="product-info__row">
      <div className={titleClass}>{title}</div>
      <div className="product-info__value">{value}</div>
    </div>
  )
}

const ProductOptionCard: FunctionComponent<Props> = ({
  data,
  selected,
  onSelect,
  selectedSurcharges = [],
  discount,
}) => {
  const {
    deductible,
    coverage_months,
    coverage_miles,
    surcharges,
    total_cost,
    productDescription,
    monthly_payment,
    monthly_membership,
    productIsNew
  } = data

  // A monthly membership renews indefinitely, so its real term and mileage are
  // never shown -- "UNLIMITED" is purely a display concern.
  const formatMonths = (months) => {
    return monthly_membership ? 'UNLIMITED' : `${months} months`
  }
  const formatMiles = (miles) => {
    return monthly_membership ? 'UNLIMITED' : `${miles} miles`
  }
  const formatCost = (cost) => {
    const formattedPrice = Number(cost).toFixed(2)
    return `$${formattedPrice}`
  }
  const formatAddons = (sur) => {
    const surchargesFiltered = sur.filter((s) =>
      selectedSurcharges.includes(s.SurchargeCode)
    )
    if (surchargesFiltered.length === 0) return <p> No Add-on selected</p>

    return surchargesFiltered.map((s, i) => {
      const formattedPrice = Number(s.Price).toFixed(0).replace('.', '')
      return (
        <div key={i}>
          <p>
            {s.Description} (+${formattedPrice})
          </p>
        </div>
      )
    })
  }
  const onClickCard = () => {
    onSelect(data)
  }
  const gridClassName = selected
    ? 'product-selection-card__selected'
    : undefined
  const surchargesValues = calculateSurcharges(
    surcharges,
    selectedSurcharges,
    'Price',
    'MonthlyPayment'
  )

  const { totalCost, monthlyCost } = useMemo(
    () =>
      calculateProductPricing({
        total_cost,
        monthly_payment,
        surchargesValues,
        discount,
        coverage_months,
        monthly_membership,
      }),
    [
      total_cost,
      monthly_payment,
      surchargesValues,
      discount,
      coverage_months,
      monthly_membership,
    ]
  )

  const totalCostFormatted = formatCost(totalCost)
  const monthlyValueCost = formatCost(monthlyCost)

  // NOTE: disabling Monthly Payment for MVP as its wrong
  const showMonthlyPayment = false

  return (
    <div
      key={data.id}
      data-testid={`product-option-${data.id}`}
      onClick={onClickCard}
    >
      <ProductSelectionCard
        title={productDescription}
        is_new={productIsNew}
        selected
        gridClass={gridClassName}
      >
        <ProductInfoRow title={'Deductible'} value={deductible} />
        <ProductInfoRow
          title={'Coverage Period'}
          value={formatMonths(coverage_months)}
        />
        <ProductInfoRow title={'Mileage'} value={formatMiles(coverage_miles)} />
        <ProductInfoRow
          title={'Add-ons'}
          value={formatAddons(surcharges)}
          stickyTitle
        />
        {showMonthlyPayment && (
          <ProductInfoRow title={'Monthly Payment'} value={monthlyValueCost} />
        )}
        <ProductInfoRow
          title={monthly_membership ? 'MONTHLY' : 'PRICE'}
          value={totalCostFormatted}
        />
      </ProductSelectionCard>
    </div>
  )
}

ProductOptionCard.displayName = 'ProductOptionCard'

export default ProductOptionCard
